"use client";

// UserChip — you, top-right, on every innernet surface. ONE chip, ONE size,
// ONE behaviour.
//
// Signed in on the dashboard, the docs, the blog, a skill page, the admin
// console, anywhere: your name and your face sit where the "enter" door sits
// for a stranger, so a signed-in person never has to wonder whether this page
// knows them. The mark is the dashboard's exact lockup — the name at the crumb
// size, the cell at 48 (38 on a phone, where the name also steps away) — and
// pressing it does one thing everywhere: it opens THE YOU-MENU.
//
// THE YOU-MENU (John, 2026-10-22): "clicking on it opens a blurred, progressive
// veil, just like the rest of our UI… a right-aligned vertical stack of a few
// options that lead you directly to your settings, you, and your home… the
// admin panel as well, for founders." A veil of the page's own ground, its blur
// strongest at the right edge and falling away to the left (progressive), and
// under the chip a right-aligned stack: home · you · settings · (admin panel,
// founders only) · sign out, entering on the house `--i` stagger. Esc, a press
// on the veil, or the chip again closes it.
//
// It is the same everywhere, which is the point: one mark, one behaviour. An
// older takeover (2026-09) drove netti and the ≡'s veil and fought the page
// under it (the company page glitched on every press); this menu touches
// neither. It is a portal of its own with ONE frost (the veil), no organism
// drive, no scroll lock, so nothing under it re-lays out.
//
// It asks /api/me/card for itself rather than being handed a viewer, because it
// mounts on statically rendered pages too (law: the chrome must not force a
// page dynamic). A host that already knows who is looking (the dashboard, the
// admin console) hands the card in and skips the fetch. Signed out it renders
// only the `fallback`.

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import CellAvatar from "./CellAvatar";
import { dashboardHref, isCrossHost } from "../lib/nav/siteNav";

/** where a row of the you-menu goes. The dashboard steps its own shell there
 *  in place (`onGo`); everywhere else these are the dashboard's own addresses. */
export type YouPlace = "home" | "you" | "settings";
const PLACE_PATH: Record<YouPlace, string> = {
  home: "/dashboard",
  you: "/dashboard/you",
  settings: "/dashboard/settings",
};

export interface MeCard {
  signedIn: boolean;
  name: string;
  avatarUrl: string | null;
  founder: boolean;
}

/** the ONE size of the mark — the dashboard's, everywhere. */
export const CHIP_CELL = 48;
export const CHIP_CELL_NARROW = 38;
/** the phone breakpoint the dashboard shell uses for its chrome. */
export const CHIP_NARROW_QUERY = "(max-width: 640px)";

/** cached for the tab's life so moving between pages doesn't re-ask. */
let cached: MeCard | null = null;
let inflight: Promise<MeCard | null> | null = null;

export async function loadCard(): Promise<MeCard | null> {
  if (cached) return cached;
  if (!inflight) {
    inflight = fetch("/api/me/card", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: MeCard | null) => {
        cached = j && j.signedIn ? j : null;
        // the door said nobody is signed in (not a fetch that failed): what the last person left here is not for the
        // next to read, however their visit ended (a session that ran out never passed the sign-out button)
        // only a plain signed out lets the places go: a card that could not tell (unsure) keeps them
        if (j && j.signedIn === false && !(j as { unsure?: boolean }).unsure) void import("../lib/os/resume").then((m) => m.claimResumable(null)).catch(() => {});
        return cached;
      })
      .catch(() => null)
      .finally(() => { inflight = null; });
  }
  return inflight;
}

/** forget the cached card — sign-out calls this so the chip doesn't linger. */
export function forgetCard() { cached = null; }

/** true under the dashboard's phone breakpoint — the cell steps down to 38. */
export function useChipNarrow(): boolean {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(CHIP_NARROW_QUERY);
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return narrow;
}

export default function UserChip({
  fallback = null,
  card,
  preview,
  onGo,
  avatarRef,
  className,
  style,
}: {
  /** what stands in this slot for a stranger — usually the "enter" door. */
  fallback?: React.ReactNode;
  /** a host that already knows who is looking hands the card in — no fetch. */
  card?: MeCard | null;
  /** stand in for the fetch (the dev chrome lab). Never set in the product. */
  preview?: MeCard;
  /** the dashboard is already home: it steps its shell to the place in place
   *  instead of navigating. Everywhere else a row loads the dashboard there. */
  onGo?: (place: YouPlace) => void;
  /** the dashboard measures the cell's centre to seat netti in the you space. */
  avatarRef?: React.Ref<HTMLSpanElement>;
  className?: string;
  style?: React.CSSProperties;
}) {
  const given = card ?? preview ?? null;
  // the fetched card (only consulted when no host handed one in)
  const [fetched, setFetched] = useState<{ card: MeCard | null; done: boolean }>({ card: cached, done: cached !== null });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const chipRef = useRef<HTMLAnchorElement>(null);
  const router = useRouter();
  const narrow = useChipNarrow();

  useEffect(() => {
    if (given) return;
    let live = true;
    loadCard().then((c) => { if (live) setFetched({ card: c, done: true }); });
    return () => { live = false; };
  }, [given]);

  const me = given ?? fetched.card;
  const resolved = !!given || fetched.done;

  // where the press goes, named for THIS host (the app host's own path, or the
  // app host by name from the apex): a real link, so a modified click opens a
  // new tab and a plain one always arrives
  const [href, setHref] = useState("/dashboard");
  useEffect(() => setHref(dashboardHref()), []);

  // a page restored from the back-forward cache keeps its last state: never a
  // chip still dimmed from the press that left it
  useEffect(() => {
    const onShow = () => setBusy(false);
    window.addEventListener("pageshow", onShow);
    return () => window.removeEventListener("pageshow", onShow);
  }, []);

  // law_always_there — this slot never pops or flips. Until we know who is
  // looking it holds its space at zero opacity, then the right thing FADES in.
  // Painting the "enter" door first and swapping it for a face (or the reverse)
  // is the one thing worse than a beat of quiet: chrome that was briefly wrong.
  if (!me) {
    return (
      <span className={`user-slot${className ? ` ${className}` : ""}`} data-resolved={resolved || undefined} style={style}>
        {fallback}
      </span>
    );
  }

  const press = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // a new tab or window is the browser's to open, from the real href
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    setOpen((o) => !o);
  };

  // a row: leave for the place, the plain way when it is on another host
  const go = (place: YouPlace) => {
    setOpen(false);
    if (onGo) { onGo(place); return; }
    setBusy(true);
    const to = dashboardHref(PLACE_PATH[place]);
    // another host (the apex → yours.innernet.live): a real page load. A router
    // push there rides the proxy's cross-origin 307, which a client fetch cannot
    // always follow, and the press looked like it did nothing.
    if (isCrossHost(to)) { window.location.assign(to); return; }
    router.push(to);
    // and if the router has not left this page in a while (a stalled
    // transition, a prefetch that never answered), go the plain way. Long
    // enough that a slow dashboard is never interrupted mid-arrival.
    const from = window.location.href;
    window.setTimeout(() => {
      if (window.location.href === from) window.location.assign(to);
    }, 6000);
  };

  return (
    <span className={`user-slot${className ? ` ${className}` : ""}`} data-resolved style={style}>
      <a
        href={href}
        ref={chipRef}
        className="user-chip"
        title="you"
        aria-label={`you, ${me.name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        data-noswipe
        data-busy={busy || undefined}
        data-open={open || undefined}
        onClick={press}
      >
        <span className="user-chip__name os-username">{me.name}</span>
        {/* the profile mark wears the innernet CELL silhouette (law_cell) — one
            shared CellAvatar, sized to the dashboard crumb everywhere. Wrapped
            so its centre can be measured — CellAvatar takes no ref. */}
        <span ref={avatarRef} style={{ display: "inline-flex", lineHeight: 0 }}>
          <CellAvatar
            name={me.name}
            src={me.avatarUrl}
            className="os-avatar"
            size={narrow ? CHIP_CELL_NARROW : CHIP_CELL}
            fontSize="var(--t-crumb)"
          />
        </span>
      </a>
      <YouMenu open={open} anchor={chipRef} me={me} cell={narrow ? CHIP_CELL_NARROW : CHIP_CELL} onGo={go} onClose={close} />
    </span>
  );
}

/** THE YOU-MENU — the veil and the stack, portalled to the body. */
function YouMenu({
  open,
  anchor,
  me,
  cell,
  onGo,
  onClose,
}: {
  open: boolean;
  anchor: React.RefObject<HTMLAnchorElement | null>;
  me: MeCard;
  cell: number;
  onGo: (place: YouPlace) => void;
  onClose: () => void;
}) {
  // mounted through its way out, so the veil can lift instead of vanishing
  const [shown, setShown] = useState(false);
  const [live, setLive] = useState(false);
  const [at, setAt] = useState<{ top: number; right: number; chip: { top: number; left: number; height: number } } | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // measured and mounted on the next frame, uncovered on the one after, so
    // the veil and the rows always ease in from their resting state
    let raf2 = 0;
    if (open) {
      const raf = requestAnimationFrame(() => {
        const r = anchor.current?.getBoundingClientRect();
        if (r) setAt({ top: r.bottom + 22, right: Math.max(16, document.documentElement.clientWidth - r.right), chip: { top: r.top, left: r.left, height: r.height } });
        setLive(true);
        raf2 = requestAnimationFrame(() => setShown(true));
      });
      return () => { cancelAnimationFrame(raf); cancelAnimationFrame(raf2); };
    }
    const raf = requestAnimationFrame(() => setShown(false));
    const t = window.setTimeout(() => setLive(false), 440);
    return () => { cancelAnimationFrame(raf); window.clearTimeout(t); };
  }, [open, anchor]);

  // Esc closes and hands focus back to the chip; a keyboard lands on the first row
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onClose();
      anchor.current?.focus({ preventScroll: true });
    };
    const onResize = () => onClose();
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("resize", onResize);
    const t = window.setTimeout(() => listRef.current?.querySelector<HTMLElement>("button")?.focus({ preventScroll: true }), 60);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("resize", onResize);
      window.clearTimeout(t);
    };
  }, [open, onClose, anchor]);

  const signOut = async () => {
    onClose();
    const [{ createClient }, { forgetRoomCaches }, { forgetAllResumable }] = await Promise.all([
      import("../lib/supabase/client"),
      import("./ours/room/roomCaches"),
      import("../lib/os/resume"),
    ]);
    await createClient().auth.signOut();
    // the chip caches who is looking for the tab's life, and the room's page
    // what it read: neither may answer the next person on this tab
    forgetCard();
    forgetRoomCaches();
    // nor may a place kept in this browser: an unsent line, a name being typed, where a page was read
    forgetAllResumable();
    window.location.assign("/");
  };

  if (!live || typeof document === "undefined") return null;
  const rows: { key: string; label: string; act: () => void }[] = [
    { key: "home", label: "home", act: () => onGo("home") },
    { key: "you", label: "you", act: () => onGo("you") },
    { key: "settings", label: "settings", act: () => onGo("settings") },
  ];
  if (me.founder) rows.push({ key: "admin", label: "admin panel", act: () => { onClose(); window.location.assign("/admin"); } });

  return createPortal(
    <div className="you-menu" data-shown={shown || undefined}>
      <div className="you-menu__veil" aria-hidden onClick={onClose} />
      {/* you stay where you pressed: the chip, seated over the veil exactly on
          its own place (the header's stacking keeps the real one beneath), and
          pressing it again closes the menu */}
      {at && (
        <button
          type="button"
          className="user-chip you-menu__chip"
          aria-label="close"
          style={{ top: at.chip.top, left: at.chip.left, height: at.chip.height }}
          onClick={() => { onClose(); anchor.current?.focus({ preventScroll: true }); }}
        >
          <span className="user-chip__name os-username">{me.name}</span>
          <CellAvatar name={me.name} src={me.avatarUrl} className="os-avatar" size={cell} fontSize="var(--t-crumb)" />
        </button>
      )}
      <div
        ref={listRef}
        className="you-menu__list"
        role="menu"
        aria-label="you"
        style={at ? { top: at.top, right: at.right } : undefined}
      >
        {rows.map((r, i) => (
          <button key={r.key} type="button" role="menuitem" className="you-menu__row" style={{ "--i": i } as React.CSSProperties} onClick={r.act}>
            {r.label}
          </button>
        ))}
        <button
          type="button"
          role="menuitem"
          className="you-menu__row you-menu__row--quiet"
          style={{ "--i": rows.length } as React.CSSProperties}
          onClick={() => void signOut()}
        >
          sign out
        </button>
      </div>
    </div>,
    document.body,
  );
}
